import React, { useContext, useMemo } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import styled from 'styled-components';
import { Redirect } from 'react-router';
import { useParams } from 'react-router-dom';

import CategoryFilters from 'components/common/CategoryFilters';

import {
  allowMarketplace,
  selectDefaultWallet,
  getActiveEmployeeWallets,
} from 'utils/employee';
import { filterActivePretaxAccountsWithFsaType } from 'utils/pretaxAccount';
import { AccountRoutes } from 'utils/routeGenerator';

import ProfileContext from 'contexts/ProfileContext';
import PretaxContext from 'contexts/PretaxContext';
import type { PretaxAccount } from 'contexts/PretaxContext/types';

import useQueryCategoriesWithProducts from 'hooks/useQueryCategoriesWithProducts';

import Paper from '@twicapp/ui/components/Paper';
import { SCREEN_WIDTHS } from '@twicapp/ui/constants/breakpoints';

import { FSA_STORE_PRETAX_ACCOUNT_TYPES } from 'constants/accounts';

const Container = styled(Paper)`
  padding-top: 80px;
  display: flex;
  gap: 40px;
  height: 100%;
  max-width: 1102px;
  margin: 0 auto;

  @media (max-width: ${SCREEN_WIDTHS.DESKTOP}) {
    margin-left: 75px;
  }

  @media (max-width: ${SCREEN_WIDTHS.LAPTOP}) {
    max-width: 892px;
    margin: 0 auto;
  }

  @media (max-width: ${SCREEN_WIDTHS.TABLET_PORTRAIT}) {
    max-width: 634px;
    margin-left: 72px;
  }
`;

const StoreLayout = ({ children }: $TSFixMe) => {
  const { employee, company } = useContext(ProfileContext);
  const { pretax_accounts, is_loading_pretax } = useContext(PretaxContext);
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'employee_wallet_id' does not exist on ty... Remove this comment to see the full error message
  const { employee_wallet_id } = useParams();
  const handleError = useErrorHandler();
  const {
    settings: { country, is_posttax_enabled },
    employee_wallets,
  } = employee;

  const active_employee_wallets = useMemo(() => {
    if (!allowMarketplace(employee, company)) {
      return [];
    }
    const { company_wallet_configurations } = company;
    return getActiveEmployeeWallets({
      company_wallet_configurations,
      employer_sponsored_wallets: employee_wallets,
    });
  }, [company, employee_wallets, employee]);

  const results = useQueryCategoriesWithProducts({
    employee_wallets: active_employee_wallets,
    country,
    onError: (error: Error) => handleError(error),
  });
  const categories_with_vendors = allowMarketplace(employee, company)
    ? results.data ?? []
    : [];

  //We only want to show one pre_tax account, either FSA, HSA, or LPFSA.
  const active_pretax_accounts =
    filterActivePretaxAccountsWithFsaType<PretaxAccount>({
      pretax_accounts,
    });

  const filtered_wallet = employee_wallet_id
    ? employee_wallets.find(({ id }: $TSFixMe) => id === employee_wallet_id)
    : selectDefaultWallet({ employee_wallets }) || {};

  if (
    !is_posttax_enabled &&
    !allowMarketplace(employee, company) &&
    !is_loading_pretax &&
    active_pretax_accounts.length === 0
  ) {
    return <Redirect to={AccountRoutes.list()} />;
  }

  const fsa_and_lpf = active_pretax_accounts.find(
    ({ account_type }) =>
      account_type.toLowerCase() === FSA_STORE_PRETAX_ACCOUNT_TYPES.LPF ||
      account_type.toLowerCase() === FSA_STORE_PRETAX_ACCOUNT_TYPES.LPFSA ||
      account_type.toLowerCase() === FSA_STORE_PRETAX_ACCOUNT_TYPES.LFS
  );

  return (
    <Container>
      <CategoryFilters
        employee_wallets={active_employee_wallets}
        filtered_wallet={filtered_wallet}
        categories_with_vendors={categories_with_vendors}
        pretax_accounts={active_pretax_accounts}
        has_fsa_and_lpf={Boolean(fsa_and_lpf)}
      />
      {children}
    </Container>
  );
};

export default StoreLayout;
